import { onMounted, onBeforeUnmount, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import commons from '@/utils/Common'
import { BASE_API } from '@/config/app'
import { WALLET_V1_POINTS_STREAM } from '@/api/const/wallet'
import {
  fetchLoyaltyBalance,
  formatLoyaltyCompact,
  formatLoyaltyFull,
  getLoyaltySnapshot,
  onLoyaltyPointsChange,
  resolveLoyaltyUserIds,
  setLoyaltyBalance,
  type LoyaltyTierId,
} from '@/utils/loyaltyPoints'

const TIER_I18N: Record<LoyaltyTierId, string> = {
  bronze: 'points.tierBronze',
  silver: 'points.tierSilver',
  gold: 'points.tierGold',
  diamond: 'points.tierDiamond',
}

const authHeader = (): string | null => {
  const raw = commons.getToken?.()
  if (raw == null || raw === '') return null
  try {
    const token = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (!token?.accessToken) return null
    return `${token.tokenType || 'Bearer'} ${token.accessToken}`
  } catch {
    return null
  }
}

const parseSseBlock = (block: string): { event: string; data: string } | null => {
  let event = 'message'
  const dataLines: string[] = []
  for (const line of block.split(/\r?\n/)) {
    if (!line || line.startsWith(':')) continue
    if (line.startsWith('event:')) event = line.slice(6).trim()
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart())
  }
  if (!dataLines.length) return null
  return { event, data: dataLines.join('\n') }
}

export function useLoyaltyPoints() {
  const { t, locale } = useI18n()
  const balance = ref(0)
  const tierId = ref<LoyaltyTierId>('bronze')

  const refresh = () => {
    const snap = getLoyaltySnapshot()
    balance.value = snap.balance
    tierId.value = snap.tierId
  }

  /** Số dư thật từ wallet-engine (SoR) — chip không dùng số local giả. */
  const fetchBalance = async () => {
    const next = await fetchLoyaltyBalance()
    if (next != null) refresh()
  }

  const applySnap = (snap: { balance: number; tierId?: LoyaltyTierId }) => {
    balance.value = snap.balance
    tierId.value = snap.tierId || getLoyaltySnapshot().tierId
  }

  const balanceFull = computed(() =>
    formatLoyaltyFull(balance.value, locale.value === 'us' ? 'en-US' : 'vi-VN')
  )
  const balanceCompact = computed(() => formatLoyaltyCompact(balance.value))
  const tierName = computed(() => t(TIER_I18N[tierId.value] || TIER_I18N.bronze))

  let off: (() => void) | null = null
  let abort: AbortController | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let reconnectMs = 2000
  let sseKey = ''

  const stopSse = () => {
    sseKey = ''
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    abort?.abort()
    abort = null
  }

  const handleSseData = (eventName: string, raw: string) => {
    if (eventName !== 'point-updated' && eventName !== 'message') return
    try {
      const payload = JSON.parse(raw)
      if (!payload || typeof payload !== 'object') return
      // Chỉ nhận số dư tuyệt đối; delta có thể trùng với lần cộng ở Reward.vue.
      const abs = Number(payload.balance ?? payload.balanceAfter)
      if (Number.isFinite(abs) && abs >= 0) {
        setLoyaltyBalance(abs)
        applySnap(getLoyaltySnapshot())
        return
      }
      void fetchBalance()
    } catch {
      /* ignore malformed frames */
    }
  }

  const pumpSse = async (url: string, signal: AbortSignal) => {
    const auth = authHeader()
    if (!commons.checkLogin?.() || !auth) {
      throw new Error('SSE skipped: not logged in')
    }
    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
      Authorization: auth,
    }

    const res = await fetch(url, { method: 'GET', headers, credentials: 'include', signal })
    if (!res.ok || !res.body) {
      throw new Error(`SSE ${res.status}`)
    }

    reconnectMs = 2000
    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buf = ''

    while (!signal.aborted) {
      const { done, value } = await reader.read()
      if (done) break
      buf += decoder.decode(value, { stream: true })
      const blocks = buf.split(/\r?\n\r?\n/)
      buf = blocks.pop() || ''
      for (const block of blocks) {
        const parsed = parseSseBlock(block)
        if (parsed) handleSseData(parsed.event, parsed.data)
      }
    }
  }

  const connectSse = () => {
    // Chỉ mở SSE sau khi user đã đăng nhập thành công (có session + access token).
    if (!commons.checkLogin?.() || !authHeader()) {
      stopSse()
      return
    }
    const { tenantId, userId } = resolveLoyaltyUserIds()
    if (tenantId == null || userId == null) {
      stopSse()
      return
    }
    const nextKey = `${tenantId}:${userId}`
    if (sseKey === nextKey && abort && !abort.signal.aborted) {
      return
    }
    stopSse()
    sseKey = nextKey
    const controller = new AbortController()
    abort = controller
    const base = String(BASE_API || '').replace(/\/$/, '')
    const url = `${base}${WALLET_V1_POINTS_STREAM}?tenantId=${tenantId}&userId=${userId}`

    const loop = async () => {
      while (abort === controller && !controller.signal.aborted) {
        if (!commons.checkLogin?.() || !authHeader()) {
          stopSse()
          return
        }
        try {
          await pumpSse(url, controller.signal)
        } catch {
          if (controller.signal.aborted) return
        }
        if (controller.signal.aborted) return
        await new Promise<void>((resolve) => {
          reconnectTimer = setTimeout(resolve, reconnectMs)
        })
        reconnectMs = Math.min(reconnectMs * 2, 15000)
      }
    }
    void loop()
  }

  onMounted(() => {
    refresh()
    off = onLoyaltyPointsChange((snap) => applySnap(snap))
    if (commons.checkLogin?.()) {
      void fetchBalance()
    }
  })

  onBeforeUnmount(() => {
    stopSse()
    off?.()
  })

  return {
    balance,
    tierId,
    balanceFull,
    balanceCompact,
    tierName,
    refresh,
    fetchBalance,
    connectSse,
    disconnectSse: stopSse,
  }
}
